import React, { memo, ReactNode, useCallback } from 'react';
import { MarkdownRenderer } from '../message/markdown/MarkdownRenderer';
import { WidgetRenderProps } from '../message/types';
import { AiBundleHandoffWidget } from '../message/widgets/AiBundleHandoffWidget';
import { ApdmCampaignPreviewWidget } from '../message/widgets/ApdmCampaignPreviewWidget';
import { ApdmImportMappingWidget } from '../message/widgets/ApdmImportMappingWidget';
import { BiDashboardChangesWidget } from '../message/widgets/bi-dashboard-changes';
import { BiDashboardChoiceWidget } from '../message/widgets/bi-dashboard-choice';
import { BiDatasetChoiceWidget } from '../message/widgets/bi-dataset-choice';
import { BiInsightProposalsWidget } from '../message/widgets/bi-insight-proposals';
import { BiUploadWidget } from '../message/widgets/bi-upload';
import { BiVisualizationWidget } from '../message/widgets/bi-visualization';
import { ChartWidget, ChartWidthContext } from '../message/widgets/ChartWidget';
import { ClientActionWidget } from '../message/widgets/ClientActionWidget';
import { FacadeWidget } from '../message/widgets/FacadeWidget';
import { GaugeWidget } from '../message/widgets/GaugeWidget';
import { WidgetLoadingPlaceholder } from '../message/widgets/LoadingPlaceholder';
import { MilestoneWidget } from '../message/widgets/MilestoneWidget';
import { OrderCardWidget } from '../message/widgets/OrderCardWidget';
import { ReportLinkWidget } from '../message/widgets/ReportLinkWidget';
import { SuggestedQuestionsWidget } from '../message/widgets/SuggestedQuestionsWidget';
import { TechnicianWorkloadWidget } from '../message/widgets/TechnicianWorkloadWidget';
import { VehicleCardWidget } from '../message/widgets/VehicleCardWidget';
import { WorkflowProgressWidget } from '../message/widgets/WorkflowProgressWidget';

/** Widget types `AIContentRenderer` knows how to render. */
export type AIWidgetType =
  | 'ai_bundle_handoff'
  | 'apdm_campaign_preview'
  | 'apdm_import_mapping'
  | 'bi_visualization'
  | 'bi_dashboard_choice'
  | 'bi_dashboard_changes'
  | 'bi_upload'
  | 'bi_insight_proposals'
  | 'bi_dataset_choice'
  | 'order_card'
  | 'workflow_progress'
  | 'vehicle_card'
  | 'technician_workload'
  | 'suggested_questions'
  | 'client_action'
  | 'chart'
  | 'gauge'
  | 'report_link'
  | 'facade'
  | 'milestone';

type Props = {
  content: string;
  onSuggestionPress?: (text: string) => void;
  /**
   * Widget types to suppress in this context — e.g. a shared AI reply in a
   * chat message passes `['suggested_questions']` since follow-up prompts
   * don't belong there. Disabled widgets render nothing.
   */
  disabledWidgets?: AIWidgetType[];
  /**
   * Width available to the content (e.g. a chat bubble's inner width). When
   * set, charts size to it instead of the full window so the content doesn't
   * force its host full-width. Omit in the full-width AI panel.
   */
  contentWidth?: number;
  /**
   * Stable id for the host message. Required for `client_action` to be
   * single-fire across remounts within a session.
   */
  messageId?: string;
  /**
   * Auto-fire side-effecting widgets (currently `client_action`) once
   * after stream `done`. Pass `true` for live-streamed messages, `false`
   * for history-loaded messages (otherwise scrolling back into view
   * would re-fire copy/share/etc.).
   */
  autoExecute?: boolean;
  /**
   * True while the host message is still streaming. Side-effecting
   * widgets wait until this turns false before firing.
   */
  streaming?: boolean;
};

export const AIContentRenderer = memo(
  ({
    content,
    onSuggestionPress,
    disabledWidgets,
    contentWidth,
    messageId,
    autoExecute,
    streaming,
  }: Props) => {
    // Joined key keeps `renderWidget` stable across renders even when the
    // caller passes a fresh array literal, so MarkdownRenderer stays memoized.
    const disabledKey = (disabledWidgets ?? []).join(',');
    const renderWidget = useCallback(
      (widget: string, data: any, props: WidgetRenderProps) => {
        if (disabledKey.split(',').includes(widget)) return null;
        return renderAIWidget(widget, data, props);
      },
      [disabledKey],
    );
    return (
      <ChartWidthContext.Provider value={contentWidth ?? null}>
        <MarkdownRenderer
          content={content}
          renderWidget={renderWidget}
          widgetProps={{
            onSuggestionPress,
            messageId,
            autoExecute,
            streaming,
          }}
        />
      </ChartWidthContext.Provider>
    );
  },
);

export function renderAIWidget(
  widget: string,
  data: any,
  props: WidgetRenderProps,
): ReactNode | null {
  switch (widget) {
    case 'ai_bundle_handoff':
      return <AiBundleHandoffWidget data={data} streaming={props.streaming} />;
    case 'apdm_campaign_preview':
      return (
        <ApdmCampaignPreviewWidget data={data} streaming={props.streaming} />
      );
    case 'apdm_import_mapping':
      return (
        <ApdmImportMappingWidget data={data} streaming={props.streaming} />
      );
    case 'bi_visualization':
      return <BiVisualizationWidget data={data} />;
    case 'bi_dashboard_choice':
      return <BiDashboardChoiceWidget data={data} />;
    case 'bi_dashboard_changes':
      return <BiDashboardChangesWidget data={data} />;
    case 'bi_upload':
      return <BiUploadWidget data={data} />;
    case 'bi_insight_proposals':
      return <BiInsightProposalsWidget data={data} />;
    case 'bi_dataset_choice':
      return <BiDatasetChoiceWidget data={data} />;
    case 'order_card':
      return <OrderCardWidget data={data} />;
    case 'workflow_progress':
      return <WorkflowProgressWidget data={data} />;
    case 'vehicle_card':
      return <VehicleCardWidget data={data} />;
    case 'technician_workload':
      return <TechnicianWorkloadWidget data={data} />;
    case 'suggested_questions':
      return (
        <SuggestedQuestionsWidget
          data={data}
          onPress={props.onSuggestionPress}
        />
      );
    case 'client_action':
      return (
        <ClientActionWidget
          data={data}
          messageId={props.messageId}
          blockIndex={props.blockIndex}
          autoExecute={props.autoExecute}
          streaming={props.streaming}
        />
      );
    case 'chart':
      return <ChartWidget data={data} />;
    case 'gauge':
      return <GaugeWidget data={data} />;
    case 'report_link':
      return <ReportLinkWidget data={data} />;
    case 'facade':
      return <FacadeWidget data={data} />;
    case 'milestone':
      return <MilestoneWidget data={data} />;
    case '_loading':
      if (!props.streaming && data?.tag === 'ai_bundle_handoff')
        return <AiBundleHandoffWidget data={null} />;
      if (!props.streaming && data?.tag === 'apdm_campaign_preview')
        return <ApdmCampaignPreviewWidget data={null} />;
      if (!props.streaming && data?.tag === 'apdm_import_mapping')
        return <ApdmImportMappingWidget data={null} />;
      return <WidgetLoadingPlaceholder tag={data?.tag} />;
    default:
      return null;
  }
}
